import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';

import { IMFAChallenge, TLoginResult } from './types';

export const MFA_CHALLENGE_STATUS = 202;

/**
 * What the login endpoint answered, as a decision rather than a body.
 *
 * Kept out of `authApi` so that it can be read and tested on its own: it is a
 * pure function of a response, and the module it would otherwise live in
 * cannot be imported without standing up the whole API.
 *
 * Deliberately does not trust either signal alone. The status is the
 * authoritative one, but an intermediary that rewrites `202` to `200` would
 * turn a challenge into a silent non-login, and `mfaRequired` in the body
 * survives that; conversely a body we cannot read still has a status. Whatever
 * says "not a session" wins, because that is the failure that strands a user:
 * mistaking a real session for a challenge merely shows a screen the next
 * response corrects.
 */
export const toLoginResult = (
  body: unknown,
  meta?: FetchBaseQueryMeta | { response?: { status?: number } }
): TLoginResult => {
  const challenge = (body ?? {}) as Partial<IMFAChallenge> & { mfaRequired?: boolean };
  const isChallenge =
    meta?.response?.status === MFA_CHALLENGE_STATUS || challenge.mfaRequired === true;

  if (!isChallenge) {
    return { kind: 'session' };
  }

  return {
    kind: 'mfa',
    mfaMethod: challenge.mfaMethod as IMFAChallenge['mfaMethod'],
    challengeToken: challenge.challengeToken ?? '',
    expiresIn: challenge.expiresIn ?? 0,
    detail: challenge.detail,
  };
};
